/* eslint-disable prefer-template */
import {
  IExpenseReport,
  IExpenseItem,
  isAjudaCustoReport,
} from '../pages/Dashboard/vehicles_management/Expenses';
import {
  computeAllocationValue,
  IRateioItem,
} from '../components/_components_0.2/Miles/ExpenseRateio';
import {
  getKmRate,
  isFixedRateKmObservation,
  SPECIAL_SECTOR_RATE,
} from './kmRateTable';
import { formatReportDate } from './formatReportDate';
import logo from '../assets/logo2026.png';
import { api } from '../services/api';

const getSignatureFontFamily = (style: string): string => {
  if (style === 'cursive-1') return 'Dancing Script, cursive';
  if (style === 'cursive-2') return 'Pacifico, cursive';
  if (style === 'cursive-3') return 'Great Vibes, cursive';
  if (style === 'elegant-1') return 'Playfair Display, serif';
  return 'Cormorant Garamond, serif';
};

export const generateExpensePDF = (report: IExpenseReport) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString?: string | null) =>
    formatReportDate(dateString, 'dd/MM/yyyy');

  const getSignatureStatusText = () => {
    if (report.signatureStatus === 'sent') {
      return 'Aguardando Assinatura Digital';
    }
    if (report.signatureStatus === 'pending') {
      return 'Pendente de Assinatura Digital';
    }
    return 'Sem Assinatura';
  };

  // Monta URL absoluta de um receipt (S3 retorna absoluta, disk retorna relativa)
  const getReceiptUrl = (receipt: any): string | null => {
    if (!receipt) return null;
    if (receipt.url) {
      if (receipt.url.startsWith('http')) return receipt.url;
      return `${api.defaults.baseURL}${receipt.url}`;
    }
    if (receipt.filename) {
      return `${api.defaults.baseURL}/uploads/receipts/${receipt.filename}`;
    }
    return null;
  };

  const getItemLabel = (item: IExpenseItem, index: number): string => {
    const num = index + 1;
    if (item.expense_type === 'Quilometragem adicional') {
      return `Item ${num} - ${item.expense_type} (${item.additional_km} km)`;
    }
    return `Item ${num} - ${item.expense_type} - ${formatCurrency(
      Number(item.amount) || 0,
    )}`;
  };

  const isImageMime = (mime?: string) =>
    !!mime && mime.startsWith('image/');

  const isAjudaCusto = isAjudaCustoReport(report.items);

  // Coletar itens com anexo
  const itemsWithReceipt = report.items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.receipt && !(item.receipt instanceof File));

  // Calcular KM total para determinar a faixa e a taxa
  const sectorId = (report.user as any)?.sector_id;
  const totalKmSum = report.items.reduce((s, i) => {
    if (
      i.expense_type === 'Quilometragem adicional' &&
      !isFixedRateKmObservation(i.observations)
    ) {
      return s + (Number(i.additional_km) || 0);
    }
    return s;
  }, 0);
  const KM_RATE = getKmRate(totalKmSum, sectorId);

  // Calcular totais por categoria
  const totals = {
    alimentacao: 0,
    pedagio: 0,
    outros: 0,
    ajudaCusto: 0,
    // Km "por tabela" (soma do grupo × faixa da KM_RATE_TABLE)
    kmTotal: 0,
    kmValor: 0,
    // Km com taxa fixa (diretoria/SPECIAL_FIXED_RATE_USER_IDS) — sempre
    // R$ 1,63, somado à parte para não se misturar com a faixa acima
    kmFixoTotal: 0,
    kmFixoValor: 0,
  };

  report.items.forEach(item => {
    if (item.expense_type === 'Quilometragem adicional') {
      const km = Number(item.additional_km) || 0;
      let fixedRate = KM_RATE;
      try {
        if (item.observations) {
          const parsed = JSON.parse(item.observations);
          if (parsed.km_rate) {
            fixedRate = parsed.km_rate;
          }
        }
      } catch (e) {
        // ignore
      }

      // Arredondar por item (igual ao amount persistido) — soma de valores
      // já arredondados bate com o total mostrado no app, sem diferença de
      // centavos por arredondamento acumulado.
      const valorItem = Number((km * fixedRate).toFixed(2));

      if (isFixedRateKmObservation(item.observations)) {
        totals.kmFixoTotal += km;
        totals.kmFixoValor += valorItem;
      } else {
        totals.kmTotal += km;
        totals.kmValor += valorItem;
      }
    } else if (item.expense_type === 'Refeição') {
      totals.alimentacao += Number(item.amount) || 0;
    } else if (item.expense_type === 'Pedágio adicional') {
      totals.pedagio += Number(item.amount) || 0;
    } else if (item.expense_type === 'Ajuda de custo') {
      totals.ajudaCusto += Number(item.amount) || 0;
    } else if (item.expense_type === 'Outros') {
      totals.outros += Number(item.amount) || 0;
    }
  });

  const totalGeral =
    totals.alimentacao +
    totals.pedagio +
    totals.outros +
    totals.ajudaCusto +
    totals.kmValor +
    totals.kmFixoValor;

  // Valor de um item já com taxa de KM resolvida (para o rateio "por itens")
  const getItemValueForRateio = (item: IExpenseItem): number => {
    if (item.expense_type === 'Quilometragem adicional') {
      const km = Number(item.additional_km) || 0;
      let fixedRate = KM_RATE;
      try {
        if (item.observations) {
          const parsed = JSON.parse(item.observations);
          if (parsed.km_rate) fixedRate = parsed.km_rate;
        }
      } catch (e) {
        // ignore
      }
      return km * fixedRate;
    }
    return Number(item.amount) || 0;
  };

  const rateioItems: IRateioItem[] = report.items.map(item => ({
    id: item.id,
    label: item.expense_type,
    value: getItemValueForRateio(item),
  }));

  // Linhas HTML dos participantes (responsável + vinculados)
  const participantsRows =
    report.participants && report.participants.length > 0
      ? report.participants
          .map(p => {
            const roleLabel =
              p.role === 'responsible' ? 'Responsável' : 'Vinculado';
            const payment = p.payment_required
              ? 'Pagamento necessário'
              : 'Sem pagamento (responsável arcou)';
            const sectorSection = [p.sector, p.section]
              .filter(Boolean)
              .join(' / ') || '-';
            return `
        <tr>
          <td style="border: 1px solid #ddd; padding: 6px 8px; font-weight: 600;">${p.name || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 6px 8px;">${roleLabel}</td>
          <td style="border: 1px solid #ddd; padding: 6px 8px;">${sectorSection}</td>
          <td style="border: 1px solid #ddd; padding: 6px 8px;">${p.cost_center || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 6px 8px;">${p.payment_required && p.pix_key ? p.pix_key : '-'}</td>
          <td style="border: 1px solid #ddd; padding: 6px 8px; font-size: 8px;">${payment}</td>
        </tr>`;
          })
          .join('')
      : '';

  // Linhas HTML do rateio por centro de custo
  const { rateio } = report;

  const rateioRows =
    rateio && rateio.allocations.length > 0
      ? rateio.allocations
          .map(alloc => {
            const value = computeAllocationValue(
              alloc,
              rateio.mode,
              totalGeral,
              rateioItems,
            );
            const pct =
              totalGeral > 0 ? ((value / totalGeral) * 100).toFixed(1) : '0.0';
            const sectorSection =
              [alloc.sector, alloc.section].filter(Boolean).join(' / ') || '-';
            return `
        <tr>
          <td style="border: 1px solid #ddd; padding: 6px 8px;">${alloc.cost_center || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 6px 8px;">${sectorSection}</td>
          <td style="border: 1px solid #ddd; padding: 6px 8px; text-align: right; font-weight: 600;">${formatCurrency(value)}</td>
          <td style="border: 1px solid #ddd; padding: 6px 8px; text-align: right;">${pct}%</td>
        </tr>`;
          })
          .join('')
      : '';

  const rateioModeLabels: Record<string, string> = {
    percentual: 'Percentual',
    valores: 'Valores',
    itens: 'Por itens',
  };
  const rateioModeLabel = rateio
    ? rateioModeLabels[rateio.mode] || rateio.mode
    : '';

  const totalRateado =
    rateio && rateio.allocations.length > 0
      ? rateio.allocations.reduce(
          (sum, alloc) =>
            sum +
            computeAllocationValue(alloc, rateio.mode, totalGeral, rateioItems),
          0,
        )
      : 0;

  // Criar lista de itens detalhados
  const itemsRows = report.items
    .map((item, index) => {
      let valor = '';
      let detalhes = '';

      if (item.expense_type === 'Quilometragem adicional') {
        const km = Number(item.additional_km) || 0;
        let fixedRate = KM_RATE;
        let obsTexto = '';
        const isFixedRate = isFixedRateKmObservation(item.observations);

        try {
          if (item.observations) {
            const parsed = JSON.parse(item.observations);
            if (parsed.km_rate) {
              fixedRate = parsed.km_rate;
            }
            if (parsed.obs) {
              obsTexto = parsed.obs;
            }
          }
        } catch (e) {
          obsTexto = item.observations || '';
        }

        const valorKm = km * fixedRate;
        // Km "por tabela" não tem valor individual real — o valor de cada
        // item depende da soma do grupo, só o total (resumo) é significativo.
        // Km com taxa fixa (1,63) tem valor próprio, então mostra.
        valor = isFixedRate ? formatCurrency(valorKm) : '—';
        detalhes = isFixedRate
          ? `${km} km × R$ ${fixedRate.toFixed(2)}`
          : `${km} km`;
        if (item.displacement_reason) {
          detalhes += `<br><span style="color: #666; font-size: 8px;">Motivo: ${item.displacement_reason}</span>`;
        }
        if (obsTexto) {
          detalhes += `<br><span style="color: #666; font-size: 8px;">Obs: ${obsTexto}</span>`;
        }
      } else {
        valor = formatCurrency(Number(item.amount) || 0);
        const parts: string[] = [];
        if (item.expense_type === 'Ajuda de custo') {
          try {
            const parsed = JSON.parse(item.observations || '{}');
            parts.push(`<strong style="color: #1a5490;">DADOS DO EVENTO</strong>`);
            if (parsed.nome) parts.push(`Nome: ${parsed.nome}`);
            if (parsed.objetivo) parts.push(`Objetivo: ${parsed.objetivo}`);
            if (parsed.local_evento) parts.push(`Local: ${parsed.local_evento}`);
            if (parsed.endereco) parts.push(`Endereço: ${parsed.endereco}`);
            if (parsed.periodo_evento) parts.push(`Período: ${parsed.periodo_evento}`);
            if (parsed.horarios && Array.isArray(parsed.horarios)) {
              parsed.horarios.forEach((h: any, i: number) => {
                parts.push(`Horário (${i + 1}): Início: ${h.inicio || '-'} / Término: ${h.termino || '-'}`);
              });
            }
            parts.push(`<strong style="color: #1a5490;">DADOS DA VIAGEM</strong>`);
            if (parsed.dados_viagem_data) parts.push(`Data: ${parsed.dados_viagem_data}`);
            if (parsed.trecho_ida) parts.push(`Ida: ${parsed.trecho_ida} ${(parsed.trecho_ida_data ? `(em ${parsed.trecho_ida_data})` : '')}`);
            if (parsed.trecho_volta) parts.push(`Volta: ${parsed.trecho_volta} ${(parsed.trecho_volta_data ? `(em ${parsed.trecho_volta_data})` : '')}`);
            if (parsed.descricao_valor) {
              parts.push(`<strong style="color: #1a5490;">DESCRIÇÃO DO VALOR</strong>`);
              parts.push(parsed.descricao_valor);
            }
          } catch(e) {
             if (item.observations) parts.push(item.observations);
          }
        } else if (item.observations) parts.push(item.observations);
        if (item.receipt) parts.push('Comprovante: ' + item.receipt.name);
        detalhes = parts.join('<br>') || '-';
      }

      return `
        <tr>
          <td style="border: 1px solid #ddd; padding: 6px 8px; text-align: center; color: #666;">${index + 1}</td>
          <td style="border: 1px solid #ddd; padding: 6px 8px;">${item.expense_type}</td>
          <td style="border: 1px solid #ddd; padding: 6px 8px; font-size: 9px;">${detalhes}</td>
          <td style="border: 1px solid #ddd; padding: 6px 8px; text-align: right; font-weight: 600;">${valor}</td>
        </tr>
      `;
    })
    .join('');

  // Signature font (safe fallback when no signature exists)
  const signatureFont = report.signature
    ? getSignatureFontFamily(report.signature.style)
    : 'Dancing Script, cursive';

  // Cor do status contábil
  const getStatusCssColor = () => {
    if (report.status === 'LANCADO') return '#16a34a';
    if (report.status === 'RECUSADO') return '#dc2626';
    return '#ca8a04';
  };

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Relatório de Despesas e Reembolsos - COOASGO</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&family=Pacifico&family=Great+Vibes&family=Playfair+Display:wght@400;700&family=Cormorant+Garamond:ital,wght@0,400;1,400&family=Roboto:wght@300;400;500;700&display=swap');

        @media print {
          @page {
            size: A4;
            margin: 15mm 12mm;
          }
          body {
            margin: 0;
            padding: 0;
          }
          .no-print { display: none; }
        }

        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        body {
          font-family: 'Roboto', Arial, sans-serif;
          font-size: 11px;
          line-height: 1.4;
          color: #333;
          background: white;
        }

        .header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-bottom: 10px;
          margin-bottom: 10px;
          border-bottom: 3px solid #1a5490;
        }

        .header-left {
          flex: 1;
        }

        .header-right {
          text-align: right;
          flex: 1;
        }

        .logo {
          max-width: 130px;
          max-height: 45px;
        }

        .company-name {
          font-size: 15px;
          font-weight: 700;
          color: #1a5490;
          margin-bottom: 2px;
        }

        .title {
          font-size: 16px;
          font-weight: 700;
          color: #1a5490;
          margin-bottom: 2px;
        }

        .subtitle {
          font-size: 10px;
          color: #666;
        }

        .report-date {
          font-size: 8px;
          color: #999;
          margin-top: 2px;
        }

        .info-section {
          background: #f8f9fa;
          padding: 6px 10px;
          border-radius: 3px;
          margin-bottom: 8px;
          border-left: 3px solid #1a5490;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 6px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
        }

        .info-label {
          font-weight: 600;
          font-size: 8px;
          color: #666;
          text-transform: uppercase;
          margin-bottom: 1px;
        }

        .info-value {
          font-size: 10px;
          font-weight: 500;
          color: #333;
        }

        .section-title {
          font-size: 11px;
          font-weight: 600;
          color: #1a5490;
          margin: 8px 0 5px 0;
          padding-bottom: 2px;
          border-bottom: 2px solid #1a5490;
        }

        .summary-cards {
          display: flex;
          gap: 8px;
          margin-bottom: 10px;
          flex-wrap: wrap;
        }

        .summary-card {
          flex: 1;
          min-width: 100px;
          padding: 8px 10px;
          border-radius: 4px;
          border-left: 3px solid;
        }

        .summary-card-label {
          font-size: 8px;
          font-weight: 600;
          text-transform: uppercase;
          margin-bottom: 2px;
        }

        .summary-card-value {
          font-size: 13px;
          font-weight: 700;
        }

        .summary-card-detail {
          font-size: 8px;
          margin-top: 1px;
        }

        .table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 8px;
          font-size: 10px;
        }

        .table thead {
          background: #1a5490;
          color: white;
        }

        .table th {
          padding: 5px 8px;
          text-align: left;
          font-weight: 600;
          font-size: 9px;
        }

        .table td {
          padding: 4px 8px;
          border-bottom: 1px solid #e0e0e0;
        }

        .table tr:last-child td {
          border-bottom: none;
        }

        .table .summary-row {
          background: #e3f2fd;
          font-weight: 700;
          border-top: 2px solid #1a5490;
        }

        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin: 6px 0 8px 0;
          font-size: 9px;
          border: 1px solid #ddd;
        }

        .items-table th {
          background: #37474f;
          color: white;
          padding: 4px 6px;
          text-align: left;
          font-weight: 600;
          font-size: 9px;
        }

        .items-table td {
          padding: 3px 6px;
          border-bottom: 1px solid #f0f0f0;
        }

        .signature-section {
          margin-top: 12px;
          padding-top: 8px;
          border-top: 1px solid #ddd;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 20px;
        }

        .signature-box {
          text-align: left;
          flex: 1;
        }

        .signature {
          font-family: ${signatureFont};
          font-size: 22px;
          margin: 6px 0 3px 0;
          color: #1a5490;
        }

        .signature-line {
          border-top: 1px solid #666;
          width: 220px;
          margin-bottom: 4px;
        }

        .signature-info {
          font-size: 9px;
          color: #666;
        }

        .total-box {
          background: #1a5490;
          color: white;
          padding: 10px 20px;
          border-radius: 4px;
          text-align: center;
          min-width: 220px;
        }

        .total-label {
          font-size: 9px;
          font-weight: 600;
          margin-bottom: 4px;
          letter-spacing: 0.5px;
        }

        .total-value {
          font-weight: 700;
          font-size: 22px;
        }

        .footer {
          margin-top: 8px;
          padding-top: 6px;
          border-top: 1px solid #e0e0e0;
          text-align: center;
          font-size: 8px;
          color: #999;
          line-height: 1.3;
        }

        /* Páginas de anexos */
        .attachment-page {
          page-break-before: always;
          break-before: page;
          padding-top: 10px;
          display: flex;
          flex-direction: column;
          min-height: 100vh;
        }

        .attachment-header {
          border-bottom: 2px solid #1a5490;
          padding-bottom: 8px;
          margin-bottom: 12px;
        }

        .attachment-title {
          font-size: 14px;
          font-weight: 700;
          color: #1a5490;
          margin-bottom: 2px;
        }

        .attachment-subtitle {
          font-size: 10px;
          color: #666;
        }

        .attachment-meta {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 4px;
          font-size: 9px;
          color: #666;
        }

        .attachment-meta strong {
          color: #1a5490;
          font-weight: 600;
          margin-right: 3px;
        }

        .attachment-content {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          padding: 20px;
          min-height: 500px;
        }

        .attachment-content img {
          max-width: 100%;
          max-height: 80vh;
          object-fit: contain;
        }

        .attachment-pdf-notice {
          text-align: center;
          padding: 40px 20px;
        }

        .attachment-pdf-icon {
          font-size: 48px;
          color: #dc2626;
          margin-bottom: 12px;
        }

        .attachment-pdf-text {
          font-size: 12px;
          color: #333;
          margin-bottom: 6px;
        }

        .attachment-pdf-link {
          font-size: 9px;
          color: #1a5490;
          word-break: break-all;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="header-left">
          <img src="${logo}" alt="COOASGO" class="logo" onerror="this.style.display='none'">
        </div>
        <div class="header-right">
          <div class="company-name">COOASGO</div>
          <div class="title">${isAjudaCusto ? 'AJUDA DE CUSTO' : 'RELATÓRIO DE DESPESAS E REEMBOLSOS'}</div>
          <div class="subtitle">Despesas e Quilometragem</div>
          <div class="report-date">Gerado em: ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
      </div>

      <div class="info-section">
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Usuário</span>
            <span class="info-value">${report.user?.name || report.director_name || report.director}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Data</span>
            <span class="info-value">${formatDate(report.date)}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Setor</span>
            <span class="info-value">${report.sector}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Seção</span>
            <span class="info-value">${report.section}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Centro de Custo</span>
            <span class="info-value">${report.cost_center}</span>
          </div>
          ${report.branch ? `<div class="info-item"><span class="info-label">Filial</span><span class="info-value">${report.branch.short_name}</span></div>` : ''}
          ${report.status ? `<div class="info-item"><span class="info-label">Status</span><span class="info-value" style="color: ${getStatusCssColor()}; font-weight: 700;">${report.status}</span></div>` : ''}
          ${report.due_date ? `<div class="info-item"><span class="info-label">Vencimento</span><span class="info-value">${formatDate(report.due_date)}</span></div>` : ''}
        </div>
      </div>
      ${report.status === 'RECUSADO' && report.observation ? `<div class="info-section" style="border-left-color: #dc2626; background: #fef2f2;"><div style="font-size: 9px; font-weight: 600; color: #dc2626;">MOTIVO DA RECUSA</div><div style="font-size: 10px; color: #333; margin-top: 4px;">${report.observation}</div></div>` : ''}

      ${(() => {
        const pix = report.pix_key || report.user?.pix_key;
        const bank = report.bank_name || report.user?.bank_name;
        const agency = report.bank_agency || report.user?.bank_agency;
        const account = report.bank_account || report.user?.bank_account;
        if (!pix && !bank) return '';
        return `<div class="info-section" style="border-left-color: #0284c7;">
          <div style="font-size: 8px; font-weight: 600; color: #0284c7; text-transform: uppercase; margin-bottom: 4px;">Dados Bancários para Ressarcimento</div>
          <div class="info-grid" style="grid-template-columns: repeat(4, 1fr);">
            ${pix ? `<div class="info-item"><span class="info-label">Chave PIX</span><span class="info-value">${pix}</span></div>` : ''}
            ${bank ? `<div class="info-item"><span class="info-label">Banco</span><span class="info-value">${bank}</span></div>` : ''}
            ${agency ? `<div class="info-item"><span class="info-label">Agência</span><span class="info-value">${agency}</span></div>` : ''}
            ${account ? `<div class="info-item"><span class="info-label">Conta</span><span class="info-value">${account}</span></div>` : ''}
          </div>
        </div>`;
      })()}

      ${
        participantsRows
          ? `<div class="section-title">Participantes</div>
      <table class="items-table">
        <thead>
          <tr>
            <th>Nome</th>
            <th style="width: 90px;">Papel</th>
            <th style="width: 120px;">Setor / Seção</th>
            <th style="width: 110px;">Centro de Custo</th>
            <th style="width: 120px;">Chave PIX</th>
            <th style="width: 130px;">Pagamento</th>
          </tr>
        </thead>
        <tbody>${participantsRows}</tbody>
      </table>`
          : ''
      }

      ${
        rateioRows && !isAjudaCusto
          ? `<div class="section-title">Rateio por Centro de Custo (${rateioModeLabel})</div>
      <table class="items-table">
        <thead>
          <tr>
            <th>Centro de Custo</th>
            <th style="width: 140px;">Setor / Seção</th>
            <th style="width: 110px; text-align: right;">Valor</th>
            <th style="width: 90px; text-align: right;">% do total</th>
          </tr>
        </thead>
        <tbody>
          ${rateioRows}
          <tr style="background: #f3e5f5; border-top: 2px solid #7b1fa2;">
            <td colspan="2" style="border: 1px solid #ddd; padding: 6px 8px; font-weight: 700;">TOTAL RATEADO</td>
            <td style="border: 1px solid #ddd; padding: 6px 8px; text-align: right; font-weight: 700; color: #7b1fa2;">${formatCurrency(
              totalRateado,
            )}</td>
            <td style="border: 1px solid #ddd; padding: 6px 8px;"></td>
          </tr>
        </tbody>
      </table>`
          : ''
      }

      <div class="section-title">Resumo por Categoria</div>
      <div class="summary-cards">
        ${
          totals.alimentacao > 0
            ? `<div class="summary-card" style="background: #e8f5e9; border-color: #4caf50;">
                <div class="summary-card-label" style="color: #2e7d32;">Refeições</div>
                <div class="summary-card-value" style="color: #1b5e20;">${formatCurrency(totals.alimentacao)}</div>
              </div>`
            : ''
        }
        ${
          totals.kmValor > 0
            ? `<div class="summary-card" style="background: #e3f2fd; border-color: #1976d2;">
                <div class="summary-card-label" style="color: #1565c0;">Quilometragem</div>
                <div class="summary-card-value" style="color: #0d47a1;">${formatCurrency(totals.kmValor)}</div>
                <div class="summary-card-detail" style="color: #1976d2;">${totals.kmTotal} km × R$ ${(totals.kmTotal > 0 ? totals.kmValor / totals.kmTotal : KM_RATE).toFixed(2)}</div>
              </div>`
            : ''
        }
        ${
          totals.kmFixoValor > 0
            ? `<div class="summary-card" style="background: #e3f2fd; border-color: #1976d2;">
                <div class="summary-card-label" style="color: #1565c0;">Quilometragem Tabela Fixa</div>
                <div class="summary-card-value" style="color: #0d47a1;">${formatCurrency(totals.kmFixoValor)}</div>
                <div class="summary-card-detail" style="color: #1976d2;">${totals.kmFixoTotal} km × R$ ${(totals.kmFixoTotal > 0 ? totals.kmFixoValor / totals.kmFixoTotal : SPECIAL_SECTOR_RATE).toFixed(2)}</div>
              </div>`
            : ''
        }
        ${
          totals.pedagio > 0
            ? `<div class="summary-card" style="background: #fff3e0; border-color: #f57c00;">
                <div class="summary-card-label" style="color: #e65100;">Pedágios</div>
                <div class="summary-card-value" style="color: #bf360c;">${formatCurrency(totals.pedagio)}</div>
              </div>`
            : ''
        }
        ${
          totals.ajudaCusto > 0
            ? `<div class="summary-card" style="background: #e0f2f1; border-color: #00897b;">
                <div class="summary-card-label" style="color: #00695c;">Ajuda de Custo</div>
                <div class="summary-card-value" style="color: #004d40;">${formatCurrency(totals.ajudaCusto)}</div>
              </div>`
            : ''
        }
        ${
          totals.outros > 0
            ? `<div class="summary-card" style="background: #f3e5f5; border-color: #7b1fa2;">
                <div class="summary-card-label" style="color: #6a1b9a;">Outros</div>
                <div class="summary-card-value" style="color: #4a148c;">${formatCurrency(totals.outros)}</div>
              </div>`
            : ''
        }
      </div>

      <div class="section-title">Despesas Detalhadas (${report.items.length} ${report.items.length === 1 ? 'item' : 'itens'})</div>
      <table class="items-table">
        <thead>
          <tr>
            <th style="width: 40px; text-align: center;">#</th>
            <th style="width: 140px;">Tipo</th>
            <th>Detalhes</th>
            <th style="width: 110px; text-align: right;">Valor</th>
          </tr>
        </thead>
        <tbody>
          ${itemsRows}
          <tr style="background: #e3f2fd; border-top: 2px solid #1a5490;">
            <td colspan="3" style="border: 1px solid #ddd; padding: 8px; font-weight: 700; font-size: 11px;">TOTAL GERAL</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: 700; font-size: 13px; color: #1a5490;">${formatCurrency(totalGeral)}</td>
          </tr>
        </tbody>
      </table>

      <div class="signature-section">
        <div class="signature-box">
          <div style="font-size: 8px; color: #666; text-transform: uppercase; font-weight: 600; margin-bottom: 4px;">Assinatura do Usuário</div>
          <div class="signature-line"></div>
          ${
            report.signature
              ? `<div class="signature">${report.signature.name}</div>
                 <div class="signature-info">
                   ${report.user?.name || report.director_name || ''} - ${formatDate(report.date)}
                   ${report.signature.cpf ? '<br>CPF: ' + report.signature.cpf : ''}
                   ${(report.signedAt || report.signed_at) ? '<br>Assinado em ' + formatDate((report.signedAt || report.signed_at) as string) : ''}
                 </div>`
              : `<div class="signature-info" style="text-align: center; padding: 10px 0;">
                   <strong>${report.user?.name || report.director_name || ''}</strong><br>
                   <em style="color: #999; font-size: 9px;">${getSignatureStatusText()}</em>
                 </div>`
          }
        </div>

        <div class="signature-box">
          <div style="font-size: 8px; color: #666; text-transform: uppercase; font-weight: 600; margin-bottom: 4px;">Aprovação do Gestor</div>
          <div class="signature-line"></div>
          ${
            report.managerSignature
              ? `<div class="signature" style="font-family: ${getSignatureFontFamily(report.managerSignature.style)};">${report.managerSignature.name}</div>
                 <div class="signature-info">
                   ${report.manager?.name || ''} - ${formatDate(report.date)}
                   ${report.managerSignature.cpf ? '<br>CPF: ' + report.managerSignature.cpf : ''}
                   ${(report.managerSignedAt || report.manager_signed_at) ? '<br>Aprovado em ' + formatDate((report.managerSignedAt || report.manager_signed_at) as string) : ''}
                 </div>`
              : `<div class="signature-info" style="text-align: center; padding: 10px 0;">
                   <strong>${report.manager?.name || 'Gestor'}</strong><br>
                   <em style="color: #999; font-size: 9px;">Pendente de aprovação</em>
                 </div>`
          }
        </div>
      </div>

      <div style="text-align: right; margin-top: 8px;">
        <div class="total-box" style="display: inline-block;">
          <div class="total-label">VALOR TOTAL DO REEMBOLSO</div>
          <div class="total-value">${formatCurrency(totalGeral)}</div>
        </div>
      </div>

      <div class="footer">
        Documento gerado eletronicamente em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} | COOASGO - Cooperativa de Trabalho
      </div>

      ${itemsWithReceipt
        .map(({ item, index }) => {
          const receipt = item.receipt as any;
          const url = getReceiptUrl(receipt);
          if (!url) return '';

          const displayName =
            receipt.original_name || receipt.name || 'Comprovante';
          const mime = receipt.mime_type || receipt.type;
          let size = '';
          if (receipt.size) {
            size =
              receipt.size < 1024 * 1024
                ? `${(receipt.size / 1024).toFixed(1)} KB`
                : `${(receipt.size / (1024 * 1024)).toFixed(1)} MB`;
          }
          const isImage = isImageMime(mime);

          return `
            <div class="attachment-page">
              <div class="attachment-header">
                <div class="attachment-title">Comprovante - ${getItemLabel(item, index)}</div>
                <div class="attachment-subtitle">Anexo ao relatório de despesas e reembolsos</div>
                <div class="attachment-meta">
                  <div><strong>Arquivo:</strong>${displayName}</div>
                  ${size ? `<div><strong>Tamanho:</strong>${size}</div>` : ''}
                  ${mime ? `<div><strong>Tipo:</strong>${mime}</div>` : ''}
                </div>
              </div>
              <div class="attachment-content">
                ${
                  isImage
                    ? `<img src="${url}" alt="${displayName}" />`
                    : `<div class="attachment-pdf-notice">
                         <div class="attachment-pdf-icon">📄</div>
                         <div class="attachment-pdf-text">
                           Documento PDF: <strong>${displayName}</strong>
                         </div>
                         <div class="attachment-pdf-text">
                           Acesse o comprovante através do link:
                         </div>
                         <div class="attachment-pdf-link">${url}</div>
                       </div>`
                }
              </div>
            </div>
          `;
        })
        .join('')}
    </body>
    </html>
  `;

  // Abrir em nova janela para impressão
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();

    // Esperar todas as imagens carregarem antes de imprimir
    const waitForImages = () => {
      const images = Array.from(printWindow.document.images);
      if (images.length === 0) {
        printWindow.print();
        return;
      }

      const promises = images.map(img => {
        if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
        return new Promise<void>(resolve => {
          img.addEventListener('load', () => resolve());
          img.addEventListener('error', () => resolve());
        });
      });

      Promise.all(promises).then(() => {
        setTimeout(() => printWindow.print(), 200);
      });
    };

    if (printWindow.document.readyState === 'complete') {
      waitForImages();
    } else {
      printWindow.onload = waitForImages;
    }
  }
};

